import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import { receiveError, receiveSuccess } from '../../../store/actions/notificationAction';
import { fetchFinish, fetchPending } from '../../../store/actions/fetchingAction';

import ResponseError from '../../../models/ResponseError';
import User, { UserRoleType } from '../../../models/User';

import userManagementReqHandler from '../../../api/userManagementRemote';
import UserManagement from './UserManagement';
import ActivateUserModal from './ActivateUserModal/ActivateUserModal';
import UpdateUserPasswordModal from './UpdateUserPasswordModal/UpdateUserPasswordModal';
import EditUserModal from './EditUserModal/EditUserModal';

import roles from '../../../configs/roles';
import AirlineRepModal from './AirlineRepModal/AirlineRepModal';
import AirlineType from '../../../models/AirlineType';

interface UserManagementProps {
  location?: any;
  user: User;
  receiveSuccess: Function;
  receiveError: Function;
  fetchFinish: Function;
  fetchPending: Function;
};

interface UserManagementState {
  users: User[];
  data: User[];
  inactive: boolean;
  input: {
    deactivateUserReason: string,
    newPassword: string,
    firstName: string,
    lastName: string,
    role: string,
    email: string,
    airlineValue: string
  };
  userDectivation: {
    userId: number;
    opened: boolean;
  },
  passwordResseting: {
    userId: number;
    name: string;
    email: string;
    role: string;
    opened: boolean;
  },
  editUser: {
    userId: number;
    opened: boolean;
    type: editUserType;
  },
  airlineRep: {
    userId: number;
    opened: boolean;
    type: editUserType;
  },
};

type editUserType = "create" | "update" | "reactivate";

class UserManagementContainer extends Component<UserManagementProps, UserManagementState> {

  state = {
    users: [],
    data: [],
    inactive: false,
    input: {
      deactivateUserReason: '',
      newPassword: '',
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      airlineValue: ''
    },
    userDectivation: {
      userId: 0,
      opened: false,
    },
    passwordResseting: {
      userId: 0,
      name: '',
      email: '',
      role: '',
      opened: false,
    },
    editUser: {
      userId: 0,
      opened: false,
      type: 'create' as editUserType,
    },
    airlineRep: {
      userId: 0,
      opened: false,
      type: 'create' as editUserType,
    }
  };

  componentDidMount() {

    this.getUsers();
  }

  getUsers = () => {
    this.props.fetchPending();
    userManagementReqHandler.getUsers().then(resp => {
      const data: User[] | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;

      if (error) {
        this.props.receiveError(error);
      }

      if (data) {
        this.setState({ data: data }, () => {
          this.filterUser();
        });
      }
      this.props.fetchFinish();
    })
  }

  filterUser = () => {
    let users: User[] = this.state.data.filter((u: User) => {
      if (!this.state.inactive && u.verified) {
        return u;
      }
      if (this.state.inactive && !u.verified) {
        return u;
      }
    });
    this.setState({ users });
  }

  onFilterChange = (value: boolean) => {
    this.setState({ inactive: value }, () => {
      this.filterUser();
    });
  }

  onFieldChange = (event: any, value?: string) => {
    let inputState: any = { ...this.state.input };
    inputState[event.target.name] = event.target.value;
    this.setState({ input: inputState });
  }

  cleanInput = () => {
    this.setState({
      input: {
        deactivateUserReason: '',
        newPassword: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        airlineValue: ''
      }
    });
  }

  closeUserDeactivationModals = () => {
    this.cleanInput();

    let userDectivationState = { ...this.state.userDectivation };
    userDectivationState.opened = false;
    this.setState({ userDectivation: userDectivationState });
  }

  openDectivateUserModal = (id: number) => {
    let userDectivationState = { ...this.state.userDectivation };
    userDectivationState.opened = true;
    userDectivationState.userId = id;
    this.setState({ userDectivation: userDectivationState });
  }

  deactivateUser = () => {
    this.closeUserDeactivationModals();

    userManagementReqHandler.deactivateUser(this.state.userDectivation.userId, this.state.input.deactivateUserReason)
      .then(resp => {
        const success: boolean | undefined = resp.success;
        const error: ResponseError | undefined = resp.error;

        if (error) {
          this.props.receiveError(error);
        }

        if (success) {
          this.props.receiveSuccess('Successfully deactivated user');
          this.getUsers();
        }
      });
  }

  closeResetUserPasswordModal = () => {
    this.cleanInput();

    let passwordRessetingState = { ...this.state.passwordResseting };
    passwordRessetingState.opened = false;
    this.setState({ passwordResseting: passwordRessetingState });
  }

  openResetUserPasswordModal = (user: any) => {
    let passwordRessetingState = { ...this.state.passwordResseting };
    passwordRessetingState.opened = true;
    passwordRessetingState.userId = user.id;
    passwordRessetingState.name = `${user.first_name} ${user.last_name}`;
    passwordRessetingState.email = user.email;
    passwordRessetingState.role = roles.userRoleToString(user.id_user_role);
    this.setState({ passwordResseting: passwordRessetingState });
  }

  updatePasswordAndPrint = () => {
    this.closeResetUserPasswordModal();

    userManagementReqHandler.updateUserPassword(this.state.passwordResseting.userId, this.state.input.newPassword)
      .then(resp => {
        const result = resp.result;
        const error: ResponseError | undefined = resp.error;

        if (error) {
          this.props.receiveError(error);
        }

        if (result) {
          this.props.receiveSuccess(`Successfully updated user password. A new tab 
              was opened for printing the new credentials`);
          this.getUsers();
          let win = window.open('/print-user-details?token=' + result.detailsStoreToken, '_blank');
          if (win)
            win.focus();
        }
      });
  }

  openEditUserModal = (type: editUserType, user: any | undefined) => {
    let inputState = { ...this.state.input };

    let editUserState = { ...this.state.editUser };
    editUserState.opened = true;

    if (user) {
      editUserState.userId = user.id;
      editUserState.type = type;
      inputState.email = user.email;
      inputState.lastName = user.last_name;
      inputState.firstName = user.first_name;
      let role = "office";
      if (user.id_user_role === roles.roleId.ghaWarehouse)
        role = "warehouse";
      inputState.role = role;
    } else {
      inputState.email = '';
      inputState.lastName = '';
      inputState.firstName = '';
      inputState.role = '';
    }

    this.setState({ input: inputState, editUser: editUserState });
  }

  openAirlineRepModal = (type: editUserType, user: any | undefined) => {
    let inputState = { ...this.state.input };

    let airlineRepState = { ...this.state.airlineRep };
    airlineRepState.opened = true;

    if (user) {
      airlineRepState.userId = user.id;
      airlineRepState.type = type;
      inputState.email = user.email;
      inputState.lastName = user.last_name;
      inputState.firstName = user.first_name;
      let role = "office";
      if (user.id_user_role === roles.roleId.ghaWarehouse)
        role = "warehouse";
      inputState.role = role;
    } else {
      inputState.email = '';
      inputState.lastName = '';
      inputState.firstName = '';
      inputState.role = '';
    }

    this.setState({ input: inputState, airlineRep: airlineRepState });
  }

  closeAirlineRepModal = () => {
    this.cleanInput();

    let airlineRepState = { ...this.state.airlineRep };
    airlineRepState.opened = false;
    this.setState({ airlineRep: airlineRepState });
  }

  closeEditUserModal = () => {
    this.cleanInput();

    let editUserState = { ...this.state.editUser };
    editUserState.opened = false;
    this.setState({ editUser: editUserState });
  }

  editUser = () => {
    this.closeEditUserModal();

    userManagementReqHandler.upsertUser(this.state.editUser.userId, this.state.input.firstName,
      this.state.input.lastName, this.state.input.email, this.state.input.role).then(resp => {
        const success: boolean | undefined = resp.success;
        const error: ResponseError | undefined = resp.error;

        if (error) {
          this.props.receiveError(error);
        }

        if (success) {
          let verb = this.state.editUser.type + 'd';
          this.props.receiveSuccess(`Successfully ${verb} user`);
          this.getUsers();
        }
      })
  }

  render() {

    return (
      <Fragment>
        <UserManagement
          users={this.state.users}
          inactive={this.state.inactive}
          openDectivateUserModal={this.openDectivateUserModal}
          openResetUserPasswordModal={this.openResetUserPasswordModal}
          openEditUserModal={this.openEditUserModal}
          openAirlineRepModal={this.openAirlineRepModal}
          onFilterChange={this.onFilterChange}
        />
        <ActivateUserModal
          modalOpen={this.state.userDectivation.opened}
          deactivateReason={this.state.input.deactivateUserReason}
          deactivateUser={this.deactivateUser}
          handleChange={this.onFieldChange}
          handleClose={this.closeUserDeactivationModals} />

        <UpdateUserPasswordModal
          name={this.state.passwordResseting.name}
          email={this.state.passwordResseting.email}
          role={this.state.passwordResseting.role}
          newPassword={this.state.input.newPassword}
          modalOpen={this.state.passwordResseting.opened}
          handleChange={this.onFieldChange}
          handleClose={this.closeResetUserPasswordModal}
          updateAndPrint={this.updatePasswordAndPrint}
        />
        <EditUserModal
          type={this.state.editUser.type}
          firstName={this.state.input.firstName}
          lastName={this.state.input.lastName}
          email={this.state.input.email}
          role={this.state.input.role}
          userType={this.props.user.role === 'forwarder' ? UserRoleType.FORWARDER : UserRoleType.GHA}
          modalOpen={this.state.editUser.opened}
          handleChange={this.onFieldChange}
          handleClose={this.closeEditUserModal}
          editUser={this.editUser}
        />

        {this.state.airlineRep.opened &&
          <AirlineRepModal
            airlineValue={this.state.input.airlineValue}
            modalOpen={this.state.airlineRep.opened}
            handleChange={this.onFieldChange}
            handleClose={this.closeAirlineRepModal}
            editUser={this.editUser}
            userAirlines={this.state.airlineRep}
          />}
      </Fragment>
    );
  };
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    receiveError: (error: ResponseError) => dispatch(receiveError(error)),
    receiveSuccess: (msg: string) => dispatch(receiveSuccess(msg)),
    fetchFinish: () => dispatch(fetchFinish()),
    fetchPending: () => dispatch(fetchPending())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserManagementContainer);
